/**
 * Modelo de permissoes do Family Life OS.
 *
 * Duas camadas:
 *  1. Papel (role) — o que a pessoa e na familia.
 *  2. Escopo (scope) — o que pode fazer em cada plugin, ex. 'finance:write'.
 *
 * O papel expande-se num conjunto de escopos por padrão, que depois pode ser
 * afinado por membro. Um escopo explicitamente negado ganha sempre.
 */

export type FamilyRole =
  /** Criou a familia. Nao pode ser removido nem despromovido por outros. */
  | 'owner'
  /** Gere membros e configuracoes. Tipicamente o filho/filha que administra. */
  | 'admin'
  /** A pessoa apoiada. Ve tudo o que lhe diz respeito, controla os seus dados. */
  | 'principal'
  /** Familiar de apoio. Acesso de leitura amplo, escrita em saude/compras. */
  | 'caregiver'
  /** Familiar comum. Acesso limitado ao que for partilhado. */
  | 'member'
  /** So leitura, sem dados sensiveis. Ex. um vizinho de confianca. */
  | 'viewer';

export type ScopeAction = 'read' | 'write' | 'delete' | 'admin';

/** Formato `<pluginId>:<action>`, ex. 'health:write'. `*` = todos os plugins. */
export type Scope = string;

export const ALL_ROLES: readonly FamilyRole[] = [
  'owner',
  'admin',
  'principal',
  'caregiver',
  'member',
  'viewer',
] as const;

/** Nomes em portugues para apresentacao. */
export const ROLE_LABELS: Record<FamilyRole, string> = {
  owner: 'Responsavel',
  admin: 'Administrador',
  principal: 'Titular',
  caregiver: 'Cuidador',
  member: 'Familiar',
  viewer: 'Visitante',
};

export const ROLE_DESCRIPTIONS: Record<FamilyRole, string> = {
  owner: 'Criou a familia. Tem controlo total e nao pode ser removido.',
  admin: 'Gere membros, convites e configuracoes da familia.',
  principal: 'A pessoa apoiada. Controla os seus proprios dados.',
  caregiver: 'Acompanha a saude e o dia a dia. Pode registrar e ser alertado.',
  member: 'Familiar com acesso ao que for partilhado consigo.',
  viewer: 'Apenas consulta. Sem acesso a dados sensiveis.',
};

/** Escopos concedidos por padrão a cada papel. */
export const DEFAULT_ROLE_SCOPES: Record<FamilyRole, Scope[]> = {
  owner: ['*:admin'],
  admin: ['*:read', '*:write', '*:delete', 'family:admin', 'settings:admin'],
  principal: ['*:read', '*:write', '*:delete'],
  caregiver: [
    'family:read',
    'health:read',
    'health:write',
    'shopping:read',
    'shopping:write',
    'inventory:read',
    'documents:read',
    'travel:read',
    'finance:read',
  ],
  member: ['family:read', 'shopping:read', 'shopping:write', 'travel:read'],
  viewer: ['family:read', 'shopping:read'],
};

export interface PermissionSet {
  role: FamilyRole;
  /** Escopos adicionais concedidos a este membro em concreto. */
  granted: Scope[];
  /** Escopos negados. Ganham sempre sobre `role` e `granted`. */
  denied: Scope[];
}

export function createPermissionSet(role: FamilyRole): PermissionSet {
  return { role, granted: [], denied: [] };
}

function scopeParts(scope: Scope): { plugin: string; action: string } {
  const idx = scope.indexOf(':');
  if (idx === -1) return { plugin: scope, action: 'read' };
  return { plugin: scope.slice(0, idx), action: scope.slice(idx + 1) };
}

/**
 * `admin` implica delete, que implica write, que implica read.
 * Assim `finance:admin` satisfaz um pedido de `finance:read`.
 */
const ACTION_RANK: Record<string, number> = {
  read: 1,
  write: 2,
  delete: 3,
  admin: 4,
};

function samePlugin(a: string, b: string): boolean {
  return a === '*' || a === b;
}

/**
 * Concessao: ter uma acao forte implica as mais fracas.
 * `finance:admin` satisfaz um pedido de `finance:read`.
 */
function scopeSatisfies(held: Scope, required: Scope): boolean {
  const h = scopeParts(held);
  const r = scopeParts(required);
  if (!samePlugin(h.plugin, r.plugin)) return false;
  return (ACTION_RANK[h.action] ?? 0) >= (ACTION_RANK[r.action] ?? 0);
}

/**
 * Negacao: a escada corre ao contrario, e a razao e de seguranca.
 *
 * Negar `finance:write` tem de negar tambem `finance:delete` e `finance:admin` —
 * quem nao pode escrever seguramente nao pode apagar. Mas NAO pode negar
 * `finance:read`: tirar a escrita a alguem nao e o mesmo que tirar-lhe a vista.
 *
 * Usar aqui a mesma comparacao da concessao produzia exatamente o inverso
 * disto: negar `write` bloqueava a leitura e deixava passar o `delete`. Era um
 * buraco de privilegios a serio, encontrado pelo agente do backend ao espelhar
 * esta funcao no servidor.
 */
function scopeDenies(denied: Scope, required: Scope): boolean {
  const d = scopeParts(denied);
  const r = scopeParts(required);
  if (!samePlugin(d.plugin, r.plugin)) return false;
  return (ACTION_RANK[r.action] ?? 0) >= (ACTION_RANK[d.action] ?? 0);
}

/** Todos os escopos efetivos de um conjunto de permissoes, ja com o papel expandido. */
export function effectiveScopes(perms: PermissionSet): Scope[] {
  const base = DEFAULT_ROLE_SCOPES[perms.role] ?? [];
  return [...new Set([...base, ...perms.granted])];
}

/**
 * Decide se um conjunto de permissoes autoriza um escopo.
 * Negacao explicita tem prioridade absoluta.
 */
export function can(perms: PermissionSet, required: Scope): boolean {
  for (const denied of perms.denied) {
    if (scopeDenies(denied, required)) return false;
  }
  return effectiveScopes(perms).some((held) => scopeSatisfies(held, required));
}

/** Versao que lanca — util em servicos onde a falha deve interromper. */
export function assertCan(perms: PermissionSet, required: Scope): void {
  if (!can(perms, required)) {
    throw new PermissionDeniedError(required, perms.role);
  }
}

export class PermissionDeniedError extends Error {
  readonly code = 'PERMISSION_DENIED';
  constructor(
    readonly requiredScope: Scope,
    readonly role: FamilyRole,
  ) {
    super(
      `Sem permissao para "${requiredScope}" com o papel "${ROLE_LABELS[role] ?? role}".`,
    );
    this.name = 'PermissionDeniedError';
  }
}
